import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Tag, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default logo placeholder component
const LogoPlaceholder = () => (
  <div className="w-full h-full bg-gradient-to-br from-[var(--champagne)] to-[var(--rose-gold)]/30 flex items-center justify-center">
    <div className="text-center">
      <span className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[var(--charcoal)] tracking-[0.1em]">
        DSRS
      </span>
      <div className="w-12 h-[1px] bg-[var(--gold)] mx-auto mt-2" />
    </div>
  </div>
);

// Individual Signature Card
const SignatureCard = ({ service, index, ctaLink, ctaLabel }) => {
  const isPromo = service.is_promo && service.promo_price;
  
  return (
    <Link to={`${ctaLink}#service-${service.id}`} className="block">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.2 }}
        className="group bg-white overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
      >
        {/* Promo Card Layout */}
        {isPromo ? (
          <>
            {/* Promo Header with Badge */}
            <div className="relative bg-gradient-to-r from-[var(--dark-rosegold)] to-[var(--rose-gold)] p-6 md:p-8">
              {/* Promo Badge */}
              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 flex items-center gap-2">
                <Tag size={14} className="text-white" />
                <span className="text-white text-xs uppercase tracking-wider font-medium">
                  {service.promo_type === 'percent' ? `-${service.promo_value}%` : `-${service.promo_value}€`}
                </span>
              </div>
              
              <div className="text-center">
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-white font-light tracking-wide">
                  {service.name}
                </h3>
                <div className="mt-4 flex items-center justify-center gap-3">
                  <span className="text-white/60 line-through text-lg">{service.price}€</span>
                  <span className="text-white text-3xl md:text-4xl font-light">
                    {service.promo_price.toFixed(2)}€
                  </span>
                </div>
              </div>
            </div>
            
            {/* Promo Body */}
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-3 mb-4">
                <Sparkles size={20} className="text-[var(--gold)] shrink-0 mt-1" />
                <p className="text-[var(--charcoal)]/70 leading-relaxed">
                  {service.short_description || service.description}
                </p>
              </div>
              
              {service.duration_minutes && (
                <div className="flex items-center gap-2 text-sm text-[var(--charcoal)]/50 mb-6">
                  <Clock size={14} />
                  <span>Durée : {service.duration_minutes} min</span>
                </div>
              )}
              
              <span className="inline-flex items-center gap-2 text-[var(--dark-rosegold)] text-sm uppercase tracking-[0.15em] font-medium group-hover:gap-3 transition-all">
                {ctaLabel}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </>
        ) : (
          /* Non-Promo Card Layout */
          <>
            {/* Image or Logo Header */}
            <div className="relative aspect-[5/4] overflow-hidden">
              {service.no_photo || !service.image_url ? (
                <LogoPlaceholder />
              ) : (
                <img
                  src={toAbsoluteUrl(service.image_url)}
                  alt={service.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              )}
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              {/* Title on image */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-white font-light">
                  {service.name}
                </h3>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-6 md:p-8">
              <p className="text-[var(--charcoal)]/70 leading-relaxed mb-4 line-clamp-3">
                {service.short_description || service.description}
              </p>
              
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  {service.duration_minutes && (
                    <div className="flex items-center gap-2 text-sm text-[var(--charcoal)]/50">
                      <Clock size={14} />
                      <span>{service.duration_minutes} min</span>
                    </div>
                  )}
                </div>
                <div className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)]">
                  {service.price}€
                </div>
              </div>
              
              <span className="inline-flex items-center gap-2 text-[var(--dark-rosegold)] text-sm uppercase tracking-[0.15em] font-medium group-hover:gap-3 transition-all">
                {ctaLabel}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </>
        )}
      </motion.div>
    </Link>
  );
};

// Main SignatureBlock Component
export default function SignatureBlock({ 
  categoryType, 
  title = "Nos Prestations Signature",
  subtitle,
  ctaLink = "/",
  ctaLabel = "Voir les détails"
}) {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API_URL}/services/featured/${categoryType}`);
        setServices(response.data);
      } catch (err) {
        console.error('Error fetching featured services:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeatured();
  }, [categoryType]);

  // Don't render if no featured services
  if (isLoading || services.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24 px-6 bg-gradient-to-b from-[var(--champagne)]/20 to-white" data-testid="signature-block">
      <div className="max-w-[1200px] mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[var(--charcoal)] mb-4">
            {title}
          </h2>
          <div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent mx-auto mb-4" />
          {subtitle && (
            <p className="text-[var(--charcoal)]/60 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => (
            <SignatureCard
              key={service.id}
              service={service}
              index={index}
              ctaLink={ctaLink}
              ctaLabel={ctaLabel}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
